import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, TrendingUp, Heart, Clock, Calendar, Sparkles } from 'lucide-react';
import { personalization } from '@/utils/personalization';

const PersonalizationInsightsPage = () => {
  const navigate = useNavigate();
  const [insights, setInsights] = useState(null);

  useEffect(() => {
    const data = personalization.getInsights();
    setInsights(data);
  }, []);

  if (!insights) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#2E2536]">
        <p className="text-[#EDE8F3]">Loading insights...</p>
      </div>
    );
  }

  const categoryNames = {
    healing: 'Healing',
    strength: 'Strength',
    sisterhood: 'Sisterhood',
    empowerment: 'Empowerment'
  };

  return (
    <div className="min-h-screen bg-[#2E2536]">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#2E2536]/95 backdrop-blur-sm border-b border-[#C8B5E0]/20">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={() => navigate('/profile')}
              variant="ghost"
              size="icon"
              className="text-[#EDE8F3] hover:bg-[#C8B5E0]/20 rounded-full"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            
            <div className="flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-[#E7C6C2]" />
              <h1 className="text-2xl font-bold text-[#EDE8F3]">Your Insights</h1>
            </div>
            
            <div className="w-10"></div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Affirming Message */}
        <div className="bg-gradient-to-br from-[#E7C6C2] to-[#F7E5E5] rounded-2xl p-6 mb-8 text-center">
          <Sparkles className="w-8 h-8 text-[#3E324D] mx-auto mb-3" />
          <p className="text-[#3E324D] text-lg font-medium leading-relaxed">
            {insights.affirming_message}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="bg-[#3E324D] rounded-xl p-6">
            <Calendar className="w-6 h-6 text-[#E7C6C2] mb-2" />
            <div className="text-3xl font-bold text-[#EDE8F3] mb-1">{insights.streak}</div>
            <div className="text-sm text-[#C8B5E0]">Day Streak</div>
          </div>
          
          <div className="bg-[#3E324D] rounded-xl p-6">
            <Heart className="w-6 h-6 text-[#E7C6C2] mb-2" />
            <div className="text-3xl font-bold text-[#EDE8F3] mb-1">{insights.total_days}</div>
            <div className="text-sm text-[#C8B5E0]">Total Days</div>
          </div>
          
          <div className="bg-[#3E324D] rounded-xl p-6">
            <Sparkles className="w-6 h-6 text-[#E7C6C2] mb-2" />
            <div className="text-3xl font-bold text-[#EDE8F3] mb-1">{insights.total_quotes_viewed}</div>
            <div className="text-sm text-[#C8B5E0]">Quotes Viewed</div>
          </div>
          
          <div className="bg-[#3E324D] rounded-xl p-6">
            <Clock className="w-6 h-6 text-[#E7C6C2] mb-2" />
            <div className="text-3xl font-bold text-[#EDE8F3] mb-1">{insights.total_journal_entries}</div>
            <div className="text-sm text-[#C8B5E0]">Journal Entries</div>
          </div>
        </div>

        {/* Preferred Categories */}
        <div className="bg-[#3E324D] rounded-2xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#EDE8F3] mb-4">What Resonates Most</h2>
          <p className="text-[#C8B5E0] text-sm mb-4">
            We've noticed these themes speak to you more deeply
          </p>
          <div className="space-y-3">
            {insights.preferred_categories.slice(0, 3).map((category, index) => (
              <div key={category} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#E7C6C2] flex items-center justify-center text-[#3E324D] font-bold">
                  {index + 1}
                </div>
                <span className="text-[#EDE8F3] capitalize">{categoryNames[category]}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Optimal Time */}
        {insights.most_journaling_hour !== null && (
          <div className="bg-[#3E324D] rounded-2xl p-6 mb-6">
            <h2 className="text-lg font-semibold text-[#EDE8F3] mb-4">Your Reflective Hours</h2>
            <p className="text-[#C8B5E0] mb-3">
              You tend to journal around <span className="text-[#E7C6C2] font-semibold">{insights.most_journaling_hour}:00</span>
            </p>
            <p className="text-[#C8B5E0] text-sm">
              We suggest receiving your daily whisper at <span className="text-[#E7C6C2] font-semibold">{insights.optimal_notification_time}</span> — 
              an hour before your most reflective time.
            </p>
          </div>
        )}

        {/* Info Note */}
        <div className="bg-[#3E324D]/50 rounded-xl p-4 border border-[#C8B5E0]/10">
          <p className="text-[#C8B5E0] text-sm text-center">
            💜 These insights help us personalize your experience. Your data is private and never shared.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PersonalizationInsightsPage;
